import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import {
  Button,
  Dropdown,
  Flex,
  type MenuProps,
  type TableColumnsType,
} from 'antd';
import { StatusTag } from 'pos-client/components/generic/status-tag/StatusTag.tsx';
import { PosTable } from 'pos-client/components/shared/Display/Table/PosTable.tsx';
import { LabelledDetail } from 'pos-client/components/shared/General/LabelledDetail.tsx';
import { useConfirmModal } from 'pos-client/hooks/useConfirmModal.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { usePermissions } from 'pos-client/hooks/usePermissions.tsx';
import { useRoles } from 'pos-client/hooks/useRoles.tsx';
import { useTableData } from 'pos-client/hooks/useTableData.ts';
import type { UserDetailsLocationState } from 'pos-client/pages/users/UserDetailsPage.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { UserRoleModel } from 'pos-server/backendSchemas/userBackendSchema';
import type { LocationLinkedUserFeModel } from 'pos-shared/models/locationLinkedUserSchema.ts';
import type { LocationModel } from 'pos-shared/models/locationSchema.ts';
import type { UserModel } from 'pos-shared/models/userSchema.ts';

import { UserShiftModal } from './UserShiftModal.tsx';
type UserListProps = {
  users: UserModel[];
  loading: boolean;
  isLocationOverview?: boolean;
};

export const UsersList = ({
  users,
  loading,
  isLocationOverview,
}: UserListProps) => {
  const { t } = useTranslation();
  const { hasPermissions } = usePermissions();
  const canAddAndEditUsers = hasPermissions('add.edit.users');
  const { roles } = useRoles();
  const { location } = useOutletContext<{
    location: LocationModel;
  }>();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserModel | null>(null);

  const { showSuccess, showError } = useNotifications();

  const trpcContext = trpc.useUtils();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const {
    mutateAsync: deleteLinkedUserMutation,
    isLoading: isDeletingLinkedUser,
  } = trpc.locations.deleteLocationLinkedUser.useMutation();

  const tableData = useTableData(users, (x) => x.id);

  const { showModal: confirmDeleteUser } = useConfirmModal({
    type: 'delete',
    content: t('locations.removeUserFromLocationConfirm'),
    isLoading: isDeletingLinkedUser,
    confirmText: t('common.buttons.remove'),
    confirmProps: {
      disabled: isDeletingLinkedUser,
      ghost: true,
    },
  });

  const usersActionsMenuItems = useMemo<MenuProps['items']>(() => {
    return [
      {
        label: t('common.buttons.schedule'),
        key: t('common.buttons.schedule').toLowerCase(),
      },
      {
        label: t('common.buttons.user'),
        key: t('common.buttons.user').toLowerCase(),
      },
      ...(canAddAndEditUsers && !isLocationOverview
        ? [
            {
              label: t('common.buttons.remove'),
              key: t('common.buttons.remove').toLowerCase(),
            },
          ]
        : []),
    ];
  }, [t, canAddAndEditUsers, isLocationOverview]);

  const handleUserDelete = (user: LocationLinkedUserFeModel) =>
    confirmDeleteUser({
      title: `${t('users.removeUser')} - ${user.firstName} ${user.lastName}`,
      async onConfirm() {
        return deleteLinkedUserMutation(
          { id: location.id as string, userId: user.id },
          {
            onSuccess: () => {
              showSuccess(
                t('common.notification.success.message', {
                  action: 'removed',
                  entity: 'user',
                })
              );
              trpcContext.locations.findLocationLinkedUsers.invalidate();
            },
            onError: () => {
              showError(
                t('common.notification.error.message', {
                  action: 'remove',
                  entity: 'user',
                })
              );
            },
          }
        );
      },
    });

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleOpenShiftModal = (userData: UserModel) => {
    setIsModalOpen(true);
    setSelectedUser(userData);
  };
  const handleNavigateToUserDetailsPage = (userData: UserModel) => {
    const navigationState: UserDetailsLocationState = {
      locationsBackPath: pathname,
      locationName: location.name ?? undefined,
    };
    navigate(`/users/${userData.id}/user`, {
      state: navigationState,
    });
  };

  const columns: TableColumnsType<UserModel> = [
    {
      title: t('common.people.fullName'),
      key: 'fullName',
      dataIndex: 'fullName',
    },
    {
      title: t('common.contact.emailAddress'),
      key: 'userEmail',
      dataIndex: 'userEmail',
    },
    {
      title: t('users.roles.roles'),
      key: 'roles',
      dataIndex: 'roles',
      render: (_, record) => {
        const userRoles = record.roles?.map((role) => {
          const foundRole = roles?.results?.find(
            (r: UserRoleModel) => r.id === role
          );
          return foundRole?.name;
        });
        return userRoles.join(', ') || '--';
      },
    },
    {
      title: t('common.status.status'),
      key: 'status',
      dataIndex: 'status',
      render: (_, record) =>
        record.status ? <StatusTag status={record.status} /> : '--',
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, record: UserModel) => {
        const haveTechnicianRole = record.roles?.some((role) =>
          roles?.results?.some(
            (r: UserRoleModel) => r.id === role && r.name === 'Technician'
          )
        );
        return (
          <Dropdown
            menu={{
              items: usersActionsMenuItems?.filter((item) => {
                if (haveTechnicianRole) {
                  return true;
                } else if (!haveTechnicianRole && item?.key === 'schedule') {
                  return false;
                } else return true;
              }),
              onClick: (e) => {
                if (e.key.toLowerCase() === 'schedule') {
                  handleOpenShiftModal(record);
                } else if (e.key.toLowerCase() === 'user') {
                  handleNavigateToUserDetailsPage(record);
                } else if (e.key.toLowerCase() === 'remove') {
                  handleUserDelete(record);
                }
              },
            }}
            trigger={['click']}
          >
            <Button size="small">{t('common.buttons.actions')}</Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <UserShiftModal
        user={selectedUser}
        isModalOpen={isModalOpen}
        handleCancel={handleModalClose}
      />
      <Flex gap="middle">
        <LabelledDetail
          direction="horizontal"
          label={t('locations.users.totalAssignedUsers')}
          detail={`${users.length}`}
        />
        <LabelledDetail
          direction="horizontal"
          label={t('locations.users.totalActiveAssignedUsers')}
          detail={`${users.filter((u) => u.status === 'Active').length}`}
        />
        <LabelledDetail
          direction="horizontal"
          label={t('locations.users.totalInactiveAssignedUsers')}
          detail={`${users.filter((u) => u.status !== 'Active').length}`}
        />
      </Flex>
      <PosTable
        id="location-users-summary-table"
        dataSource={tableData}
        columns={columns}
        isLoading={loading}
        fePagination
      />
    </>
  );
};
